import React from "react";
import { Card } from "primereact/card";
import { comparisonLabel } from "../../../../utils/fleetPeriods";
import { change, DASH, formatSignedNumber, formatSignedPercent } from "../../../../utils/fleetDerivations";
import type { Change } from "../../../../utils/fleetDerivations";
import type { PanelProps } from "./panelHelpers";

/**
 * BOF-76 — how energy and sessions moved against the previous period (FR-014, FR-019, FR-038 – FR-042).
 *
 * One definition everywhere: growth compares the two periods' *totals*. It is never computed on daily
 * averages — correcting for periods of different length is the 30-day lens's job alone, applied once and
 * where the reader can see it (FR-041). The workbook showed energy growth on one sheet and session growth
 * on another; this shows both, split AC / DC where known (FR-042).
 *
 * A table rather than a chart: this is a handful of signed numbers, and each carries its sign and its
 * absolute change in text, so direction never depends on colour.
 */
type Row = { measure: string; total: Change; ac: Change | null; dc: Change | null; digits: number };

const Cell: React.FC<{ value: Change | null; digits: number }> = ({ value, digits }) =>
  value == null ? (
    <td className="text-color-secondary">{DASH}</td>
  ) : (
    <td>
      <div className="font-semibold text-900">{value.percent == null ? DASH : formatSignedPercent(value.percent)}</div>
      <div className="text-xs text-color-secondary">{formatSignedNumber(value.absolute, digits)}</div>
    </td>
  );

const GrowthPanel: React.FC<Pick<PanelProps, "selected" | "previous">> = ({ selected, previous }) => {
  if (!previous) {
    return (
      <Card title="Change" className="h-full">
        <p className="text-color-secondary m-0">There is no previous period to compare against.</p>
      </Card>
    );
  }

  // AC/DC growth needs the split on both sides; otherwise one side's "AC" is really "not yet known".
  const split = selected.splitAvailable && previous.splitAvailable;
  const digits = selected.adjusted ? 2 : 0;
  const rows: Row[] = [
    {
      measure: "Energy (kWh)",
      total: change(selected.energyKwh.total, previous.energyKwh.total),
      ac: split ? change(selected.energyKwh.ac, previous.energyKwh.ac) : null,
      dc: split ? change(selected.energyKwh.dc, previous.energyKwh.dc) : null,
      digits: 0,
    },
    {
      measure: "Sessions",
      total: change(selected.sessions.total, previous.sessions.total),
      ac: split ? change(selected.sessions.ac, previous.sessions.ac) : null,
      dc: split ? change(selected.sessions.dc, previous.sessions.dc) : null,
      digits,
    },
  ];
  const incomparable = !selected.complete || !previous.complete;

  return (
    <Card title="Change" subTitle={`${selected.label} ${comparisonLabel(previous)}`} className="h-full">
      <table className="w-full" style={{ borderCollapse: "collapse" }} aria-label="Change against the previous period">
        <thead>
          <tr className="text-sm text-color-secondary text-left">
            <th className="font-semibold pb-2">Measure</th>
            <th className="font-semibold pb-2">Total</th>
            <th className="font-semibold pb-2">AC</th>
            <th className="font-semibold pb-2">DC</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.measure} className="border-top-1 surface-border" style={{ verticalAlign: "top" }}>
              <td className="py-2 text-900">{r.measure}</td>
              <Cell value={r.total} digits={r.digits} />
              <Cell value={r.ac} digits={r.digits} />
              <Cell value={r.dc} digits={r.digits} />
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-xs text-color-secondary mt-2">
        Growth compares period totals.
        {!split && " AC/DC change needs the split in both periods."}
        {incomparable && " One of these periods is still running, so the comparison covers unequal spans."}
      </div>
    </Card>
  );
};

export default GrowthPanel;
